import { Check, SkipForward, XCircle, Bell, Hand, Zap } from "lucide-react";

const RUN_STATE_STYLES = {
  proposed: "bg-gray-100 text-gray-600",
  assigned: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  awaiting_approval: "bg-purple-100 text-purple-700",
  completed: "bg-emerald-100 text-emerald-700",
  aborted: "bg-red-100 text-red-700",
};

const KIND_ICON = { manual: Hand, notify: Bell, auto_remediation: Zap };

export const RunStateBadge = ({ state }) => (
  <span
    data-testid={`run-state-badge-${state}`}
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${
      RUN_STATE_STYLES[state] || RUN_STATE_STYLES.proposed
    }`}
  >
    {state.replace(/_/g, " ")}
  </span>
);

export const PlaybookRunCard = ({
  run,
  playbook,
  user,
  riskTitle,
  onPatchStep,
  onAbort,
  onRequestRemediation,
}) => {
  const closed = run.state === "completed" || run.state === "aborted";
  const isAdmin = user?.role === "admin";
  const isAssignee = user?.user_id === run.assigned_to_user_id;
  const canAbort = (isAdmin || isAssignee) && !closed;

  const total = run.step_states.length;
  const done = run.step_states.filter((s) => s.state === "done" || s.state === "skipped").length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const stepDef = (stepNo) => playbook?.steps?.find((s) => s.step_no === stepNo);
  const canPatch = (def) =>
    !closed && (isAdmin || isAssignee || user?.role === def?.owner_role);

  return (
    <div data-testid="playbook-run" className="rounded-xl border border-black/5 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-sm font-semibold text-ek-ink">
            {playbook?.title || run.playbook_id}
          </p>
          {riskTitle && <p className="text-xs text-ek-ink/65">{riskTitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          <RunStateBadge state={run.state} />
          {canAbort && (
            <button
              onClick={() => onAbort(run.run_id)}
              data-testid="abort-run-button"
              className="text-ek-ink/30 transition-colors hover:text-red-500"
              title="Abort run"
            >
              <XCircle size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="mt-3" data-testid="run-progress">
        <div className="flex items-center justify-between text-xs text-ek-ink/65">
          <span>
            {done}/{total} steps
          </span>
          <span>{pct}%</span>
        </div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ek-canvas">
          <div
            className="h-full rounded-full bg-ek-purple transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="mt-4 space-y-2">
        {run.step_states.map((ss) => {
          const def = stepDef(ss.step_no);
          const KindIcon = KIND_ICON[def?.kind] || Hand;
          const completed = ss.state === "done" || ss.state === "skipped";
          const isAuto = def?.kind === "auto_remediation";
          return (
            <div
              key={ss.step_no}
              data-testid="step-row"
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                isAuto && !completed
                  ? "border-ek-purple/20 bg-ek-purple/[0.04]"
                  : completed
                  ? "border-black/5 bg-ek-canvas/40"
                  : "border-black/5 bg-white"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ek-purple/10 text-[11px] font-semibold text-ek-purple">
                  {ss.step_no}
                </span>
                <div>
                  <p className={`text-sm ${completed ? "text-ek-ink/65 line-through" : "text-ek-ink"}`}>
                    {def?.label || `Step ${ss.step_no}`}
                  </p>
                  <p className="flex items-center gap-1 text-[11px] text-ek-ink/65">
                    <KindIcon size={10} /> {def?.kind} · {def?.owner_role?.toUpperCase()}
                  </p>
                </div>
              </div>

              {ss.state === "pending" && canPatch(def) ? (
                isAuto ? (
                  <div className="flex items-center gap-1">
                    {onRequestRemediation && (
                      <button
                        onClick={() => onRequestRemediation(run.run_id, ss.step_no)}
                        data-testid="request-remediation-button"
                        className="flex items-center gap-1 rounded-full bg-ek-purple px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-ek-lilac"
                        title="Request auto-remediation (needs approval)"
                      >
                        <Zap size={12} /> Request
                      </button>
                    )}
                    <button
                      onClick={() => onPatchStep(run.run_id, ss.step_no, "skipped")}
                      data-testid="step-skip-button"
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100"
                      title="Skip"
                    >
                      <SkipForward size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onPatchStep(run.run_id, ss.step_no, "done")}
                      data-testid="step-done-button"
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 transition-colors hover:bg-emerald-100"
                      title="Mark done"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => onPatchStep(run.run_id, ss.step_no, "skipped")}
                      data-testid="step-skip-button"
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100"
                      title="Skip"
                    >
                      <SkipForward size={14} />
                    </button>
                  </div>
                )
              ) : (
                <span className="text-xs font-medium capitalize text-ek-ink/65">{ss.state}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
